import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Search, X, ArrowRight } from 'lucide-react';

// Cerca persona nel planning (21/10/2026, titolare): scrivi nome o cognome, vedi in quali gruppi
// del ciclo è (sede, giorno, orario, campo, allenatore) e con la freccia ci vai. Se il nome è scritto
// male propone i nomi più vicini. Vale per Elenco e Tabellone (la navigazione la fa PlanningCorsi).

const GIORNI = ['LUNEDI','MARTEDI','MERCOLEDI','GIOVEDI','VENERDI','SABATO','DOMENICA'];
const GIORNI_LABEL = { LUNEDI:'Lunedì', MARTEDI:'Martedì', MERCOLEDI:'Mercoledì', GIOVEDI:'Giovedì', VENERDI:'Venerdì', SABATO:'Sabato', DOMENICA:'Domenica' };
const norm = t => (t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9 ]/g, ' ').trim();
const parole = t => norm(t).split(/\s+/).filter(Boolean);

// distanza di modifica (refusi: "mangoni" → "mangioni")
const distanza = (a, b) => {
  if (Math.abs(a.length - b.length) > 2) return 9;
  let prec = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    for (let j = 1; j <= b.length; j++) cur[j] = Math.min(prec[j] + 1, cur[j - 1] + 1, prec[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    prec = cur;
  }
  return prec[b.length];
};

export default function CercaPersona({ ciclo, onVai, isMobile }) {
  const [q, setQ] = useState('');
  const [aperto, setAperto] = useState(false);
  const box = useRef(null);

  useEffect(() => {
    const fuori = e => { if (box.current && !box.current.contains(e.target)) setAperto(false); };
    document.addEventListener('mousedown', fuori);
    return () => document.removeEventListener('mousedown', fuori);
  }, []);

  // una voce per persona con tutti i gruppi in cui è (solo presenze attive)
  const indice = useMemo(() => {
    const m = new Map();
    (ciclo?.sedi || []).forEach(sede => (sede.slot || []).forEach(slot => (slot.atleti || []).forEach(as => {
      if (as.rimosso || !as.atleta) return;
      const id = as.atleta.id;
      if (!m.has(id)) m.set(id, { atleta: as.atleta, parole: parole(`${as.atleta.cognome} ${as.atleta.nome}`), posti: [] });
      m.get(id).posti.push({ sede, slot, as });
    })));
    m.forEach(v => v.posti.sort((a, b) => GIORNI.indexOf(a.slot.giorno) - GIORNI.indexOf(b.slot.giorno) || (a.slot.orario || '').localeCompare(b.slot.orario || '')));
    return [...m.values()];
  }, [ciclo]);

  const { trovati, forse } = useMemo(() => {
    const cerca = parole(q);
    if (!cerca.length) return { trovati: [], forse: [] };
    const nome = v => `${v.atleta.cognome} ${v.atleta.nome}`;
    // ogni parola scritta deve essere l'inizio (o un pezzo) di nome o cognome, in qualsiasi ordine
    const punti = v => {
      let tot = 0;
      for (const c of cerca) {
        if (v.parole.includes(c)) tot += 3;
        else if (v.parole.some(p => p.startsWith(c))) tot += 2;
        else if (c.length >= 3 && v.parole.some(p => p.includes(c))) tot += 1;
        else return 0;
      }
      return tot;
    };
    const trovati = indice.map(v => ({ v, p: punti(v) })).filter(x => x.p > 0)
      .sort((a, b) => b.p - a.p || nome(a.v).localeCompare(nome(b.v), 'it')).slice(0, 8).map(x => x.v);
    if (trovati.length >= 3) return { trovati, forse: [] };
    // nomi simili: ogni parola scritta vicina (1-2 lettere di differenza) a una parola del nome
    const tol = c => c.length <= 4 ? 1 : 2;
    const giaVisti = new Set(trovati);
    const forse = indice.filter(v => !giaVisti.has(v)).map(v => {
      let d = 0;
      for (const c of cerca) {
        const best = Math.min(...v.parole.map(p => Math.min(distanza(c, p), c.length < p.length ? distanza(c, p.slice(0, c.length)) + 0.5 : 9)));
        if (best > tol(c)) return null;
        d += best;
      }
      return { v, d };
    }).filter(Boolean).sort((a, b) => a.d - b.d).slice(0, 5).map(x => x.v);
    return { trovati, forse };
  }, [q, indice]);

  const vai = (posto) => { setAperto(false); onVai(posto.sede, posto.slot, posto.as); };

  const scheda = v => (
    <div key={v.atleta.id} style={{ padding:'8px 12px', borderTop:'1px solid var(--border,#eee)' }}>
      <div style={{ fontSize:'var(--font-size-sm)', fontWeight:700, color:'var(--text)' }}>
        {v.atleta.cognome} {v.atleta.nome}
        {v.posti.length > 1 && <span style={{ fontWeight:500, color:'var(--text-muted)', marginLeft:'6px', fontSize:'var(--font-size-xs)' }}>· {v.posti.length} gruppi</span>}
      </div>
      {v.posti.map(p => (
        <button key={p.as.id} onClick={() => vai(p)} title="Vai al gruppo"
          style={{ display:'flex', alignItems:'center', gap:'8px', width:'100%', textAlign:'left', marginTop:'4px', padding: isMobile ? '9px 10px' : '6px 8px', border:'1px solid var(--border2,#e5e5e5)', borderRadius:'8px', background:'var(--bg-input,#fafafa)', cursor:'pointer', color:'var(--text)', fontSize:'var(--font-size-xs)' }}>
          <span style={{ flex:1, minWidth:0, lineHeight:1.4 }}>
            <b>{p.sede.nome}</b> · {GIORNI_LABEL[p.slot.giorno] || p.slot.giorno} {p.slot.orario}
            {p.slot.campo && <> · {p.slot.campo}</>}
            {p.slot.allenatore_nome && <span style={{ color:'var(--text-muted)' }}> · 🎽 {p.slot.allenatore_nome}</span>}
            {p.slot.stato !== 'IN_CORSO' && <span style={{ color:'var(--text-dim)' }}> · bozza</span>}
          </span>
          <ArrowRight size={isMobile ? 18 : 15} color="#F4600C" style={{ flexShrink:0 }}/>
        </button>
      ))}
    </div>
  );

  const mostra = aperto && q.trim().length > 0;
  return (
    <div ref={box} style={{ position:'relative', marginBottom:'14px', maxWidth: isMobile ? '100%' : '460px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'8px', border:'1px solid var(--border2,#ddd)', borderRadius:'99px', padding: isMobile ? '9px 14px' : '6px 12px', background:'var(--bg-card,#fff)' }}>
        <Search size={15} color="var(--text-dim)"/>
        <input value={q} onChange={e => { setQ(e.target.value); setAperto(true); }} onFocus={() => setAperto(true)}
          onKeyDown={e => {
            if (e.key === 'Escape') setAperto(false);
            if (e.key === 'Enter') { const primo = (trovati[0] || forse[0])?.posti[0]; if (primo && (trovati.length + forse.length === 1 || trovati[0]?.posti.length === 1)) vai(primo); }
          }}
          placeholder={`Cerca persona in ${ciclo?.nome || 'questo ciclo'} (nome o cognome)`}
          style={{ flex:1, minWidth:0, border:'none', outline:'none', background:'transparent', color:'var(--text)', fontSize: isMobile ? '16px' : 'var(--font-size-sm)' }}/>
        {q && <button aria-label="Svuota" onClick={() => { setQ(''); setAperto(false); }} style={{ border:'none', background:'transparent', cursor:'pointer', padding:0, display:'flex', color:'var(--text-dim)' }}><X size={15}/></button>}
      </div>
      {mostra && (
        <div style={{ position:'absolute', zIndex:30, left:0, right:0, top:'calc(100% + 4px)', background:'var(--bg-card,#fff)', border:'1px solid var(--border2,#ddd)', borderRadius:'12px', boxShadow:'0 8px 24px rgba(0,0,0,.12)', maxHeight:'60vh', overflowY:'auto' }}>
          {trovati.map(scheda)}
          {forse.length > 0 && <>
            <div style={{ padding:'8px 12px 2px', fontSize:'10px', fontWeight:700, letterSpacing:'1.5px', color:'var(--text-dim)', borderTop: trovati.length ? '1px solid var(--border,#eee)' : 'none' }}>
              {trovati.length ? 'FORSE CERCAVI ANCHE' : 'NESSUNO CON QUESTO NOME · FORSE CERCAVI'}
            </div>
            {forse.map(scheda)}
          </>}
          {!trovati.length && !forse.length && (
            <div style={{ padding:'12px', fontSize:'var(--font-size-sm)', color:'var(--text-muted)' }}>Nessuna persona con questo nome nei gruppi di {ciclo?.nome}.</div>
          )}
        </div>
      )}
    </div>
  );
}
